import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, FileDown, CheckCircle2, ArrowRight, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { track } from "@/lib/analytics";
import { trackCompleteRegistration, type LeadSource } from "@/lib/leadTracking";
import {
  LeadFormSchema,
  validateLeadForm,
  validateField,
  type LeadFormErrors,
  type LeadFormField,
} from "./leadFormValidation";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Where to redirect after the download fires. Defaults to /quick-score. */
  redirectTo?: string;
  /** Tag for analytics + GHL routing. */
  source?: string;
}

export default function LeadMagnetDialog({
  open,
  onOpenChange,
  redirectTo = "/quick-score",
  source = "hero-cta",
}: Props) {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [errors, setErrors] = useState<LeadFormErrors>({});
  const [touched, setTouched] = useState<Partial<Record<LeadFormField, boolean>>>({});
  const [attempted, setAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [leadStatus, setLeadStatus] = useState<"ok" | "failed">("ok");
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);
  const downloadLinkRef = useRef<HTMLAnchorElement | null>(null);
  const safetyTimerRef = useRef<number | null>(null);

  // Live-validate quietly so the submit button can disable as soon as the form is valid.
  const isValid = useMemo(
    () => LeadFormSchema.safeParse({ first_name: firstName, email, phone }).success,
    [firstName, email, phone],
  );

  const handleBlur = (field: LeadFormField, value: string) => {
    setTouched((t) => ({ ...t, [field]: true }));
    setErrors((prev) => ({ ...prev, [field]: validateField(field, value) }));
  };
  const handleChange = (field: LeadFormField, value: string, setter: (v: string) => void) => {
    setter(value);
    // Only show errors live AFTER the user has tried to submit OR already touched the field
    // — don't yell at someone mid-typing on first attempt.
    if (attempted || touched[field]) {
      setErrors((prev) => ({ ...prev, [field]: validateField(field, value) }));
    }
  };

  // Cleanup blob URL + timers on unmount / dialog close.
  useEffect(() => {
    return () => {
      if (downloadUrl) URL.revokeObjectURL(downloadUrl);
      if (safetyTimerRef.current) window.clearTimeout(safetyTimerRef.current);
    };
  }, [downloadUrl]);

  // Track modal opens (fires once per open transition).
  useEffect(() => {
    if (open) {
      track("lead_magnet_modal_opened", { magnet: "leak-checklist", source });
    }
  }, [open, source]);

  // Auto-focus the manual download link the moment it appears so keyboard users
  // (and the user) can hit Enter to grab the PDF if the auto-trigger was blocked.
  useEffect(() => {
    if (done && downloadUrl && downloadLinkRef.current) {
      downloadLinkRef.current.focus();
    }
  }, [done, downloadUrl]);

  const goToScore = (trigger: "manual_click" | "continue_button" | "safety_timeout") => {
    if (redirecting) return;
    setRedirecting(true);
    if (safetyTimerRef.current) window.clearTimeout(safetyTimerRef.current);
    track("lead_magnet_redirect_to_quick_score", {
      magnet: "leak-checklist",
      source,
      trigger,
      redirect_to: redirectTo,
    });
    navigate(redirectTo);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAttempted(true);
    const result = validateLeadForm({ first_name: firstName, email, phone });
    if (!result.ok || !result.data) {
      setErrors(result.errors);
      setTouched({ first_name: true, email: true, phone: true });
      track("lead_magnet_form_validation_failed", {
        magnet: "leak-checklist",
        source,
        fields: Object.keys(result.errors),
      });
      const first = Object.keys(result.errors)[0] as LeadFormField | undefined;
      if (first) {
        const id = first === "first_name" ? "lm-fn" : first === "email" ? "lm-em" : "lm-ph";
        (document.getElementById(id) as HTMLInputElement | null)?.focus();
      }
      toast.error("Please fix the highlighted fields");
      return;
    }
    const parsed = { data: result.data };
    setErrors({});
    track("lead_magnet_form_submitted", {
      magnet: "leak-checklist",
      source,
      has_phone: Boolean(parsed.data.phone),
    });

    // Stash contact info so the next step (Quick Score quiz) can skip its
    // contact form and download the same PDF without re-asking.
    try {
      sessionStorage.setItem(
        "xp_lead_prefill",
        JSON.stringify({
          first_name: parsed.data.first_name,
          email: parsed.data.email,
          phone: parsed.data.phone ?? "",
          source: "lead-magnet",
        }),
      );
    } catch {
      /* sessionStorage may be unavailable in private mode — non-fatal */
    }

    setSubmitting(true);
    try {
      const fnUrl = `https://${import.meta.env.VITE_SUPABASE_PROJECT_ID}.supabase.co/functions/v1/lead-magnet-capture`;
      const res = await fetch(fnUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({
          first_name: parsed.data.first_name,
          email: parsed.data.email,
          phone: parsed.data.phone || undefined,
          magnet: "leak-checklist",
          source_url: window.location.href,
        }),
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);

      const status = (res.headers.get("x-lead-status") as "ok" | "failed" | null) ?? "failed";
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setDownloadUrl(url);

      // Best-effort auto-download — many browsers block this when not in a direct
      // click handler, so we ALWAYS render a visible "Click here" fallback below.
      let autoDownloadAttempted = false;
      try {
        const a = document.createElement("a");
        a.href = url;
        a.download = "new-patient-leak-checklist.pdf";
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
        autoDownloadAttempted = true;
      } catch {
        /* fallback link is shown regardless */
      }

      track("lead_magnet_pdf_download_started", {
        magnet: "leak-checklist",
        source,
        trigger: "auto",
        auto_attempted: autoDownloadAttempted,
        lead_status: status,
        pdf_size_bytes: blob.size,
      });
      // Map the dialog's internal `source` prop to a canonical LeadSource.
      // Default to hero — the dialog is opened from hero CTAs by default.
      const leadSource: LeadSource =
        source === "exit-intent" ? "lead-magnet-exit-intent" : "lead-magnet-hero";
      trackCompleteRegistration(leadSource, { extra: { magnet: "leak-checklist" } });
      track("lead_magnet_captured", { magnet: "leak-checklist", source, lead_status: status });
      setLeadStatus(status);
      setDone(true);

      // Safety net: if the user never clicks (download was blocked AND ignored),
      // move them to the score quiz after 30s so the funnel doesn't dead-end.
      safetyTimerRef.current = window.setTimeout(() => goToScore("safety_timeout"), 30000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong";
      track("lead_magnet_form_submit_failed", { magnet: "leak-checklist", source, error: msg });
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {done ? (
          <div className="space-y-4 text-center py-2">
            <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-600" />
            <DialogTitle>
              {leadStatus === "ok" ? "Lead saved — your checklist is ready" : "Your checklist is ready"}
            </DialogTitle>
            <DialogDescription>
              If the download didn't start automatically, click the button below to grab the PDF.
            </DialogDescription>
            {downloadUrl && (
              <a
                ref={downloadLinkRef}
                href={downloadUrl}
                download="new-patient-leak-checklist.pdf"
                onClick={() => {
                  track("lead_magnet_manual_download_click", { magnet: "leak-checklist", source });
                  track("lead_magnet_pdf_download_started", {
                    magnet: "leak-checklist",
                    source,
                    trigger: "manual",
                    lead_status: leadStatus,
                  });
                  // Give the browser a beat to start the download before navigating away.
                  window.setTimeout(() => goToScore("manual_click"), 1200);
                }}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <FileDown className="h-4 w-4" />
                Click here to download the checklist
              </a>
            )}
            <Button variant="outline" className="w-full" onClick={() => goToScore("continue_button")} disabled={redirecting}>
              {redirecting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Continue to my Practice Growth Score
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <p className="text-xs text-muted-foreground">
              We'll move you to the deeper Score after you grab the PDF.
            </p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl">Get the New Patient Leak Checklist</DialogTitle>
              <DialogDescription>
                The 7-point self-audit we use on $1M+ practices. Spot the 2 biggest leaks in your front-desk →
                consult → close flow in under 10 minutes. Instant download — no waiting.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={onSubmit} noValidate className="space-y-3 mt-2">
              <div>
                <Label htmlFor="lm-fn">First name</Label>
                <Input
                  id="lm-fn"
                  value={firstName}
                  onChange={(e) => handleChange("first_name", e.target.value, setFirstName)}
                  onBlur={(e) => handleBlur("first_name", e.target.value)}
                  required
                  maxLength={80}
                  autoComplete="given-name"
                  aria-invalid={Boolean(errors.first_name)}
                  aria-describedby={errors.first_name ? "lm-fn-err" : undefined}
                  className={errors.first_name ? "border-destructive focus-visible:ring-destructive" : undefined}
                />
                {errors.first_name && (
                  <p id="lm-fn-err" className="mt-1 flex items-center gap-1 text-xs text-destructive">
                    <AlertCircle className="h-3 w-3" /> {errors.first_name}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="lm-em">Email</Label>
                <Input
                  id="lm-em"
                  type="email"
                  inputMode="email"
                  value={email}
                  onChange={(e) => handleChange("email", e.target.value, setEmail)}
                  onBlur={(e) => handleBlur("email", e.target.value)}
                  required
                  maxLength={255}
                  autoComplete="email"
                  placeholder="you@practice.com"
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? "lm-em-err" : undefined}
                  className={errors.email ? "border-destructive focus-visible:ring-destructive" : undefined}
                />
                {errors.email && (
                  <p id="lm-em-err" className="mt-1 flex items-center gap-1 text-xs text-destructive">
                    <AlertCircle className="h-3 w-3" /> {errors.email}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="lm-ph">Phone <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Input
                  id="lm-ph"
                  type="tel"
                  inputMode="tel"
                  value={phone}
                  onChange={(e) => handleChange("phone", e.target.value, setPhone)}
                  onBlur={(e) => handleBlur("phone", e.target.value)}
                  maxLength={32}
                  autoComplete="tel"
                  placeholder="(555) 123-4567"
                  aria-invalid={Boolean(errors.phone)}
                  aria-describedby={errors.phone ? "lm-ph-err" : undefined}
                  className={errors.phone ? "border-destructive focus-visible:ring-destructive" : undefined}
                />
                {errors.phone && (
                  <p id="lm-ph-err" className="mt-1 flex items-center gap-1 text-xs text-destructive">
                    <AlertCircle className="h-3 w-3" /> {errors.phone}
                  </p>
                )}
              </div>
              <Button type="submit" className="w-full" disabled={submitting || (attempted && !isValid)}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <FileDown className="h-4 w-4 mr-2" />
                Download the checklist
              </Button>
              <p className="text-center text-xs text-muted-foreground pt-1">
                After download, we'll take you to the deeper 90-second Practice Growth Score.
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
